import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ViewDidLeave, ViewWillEnter } from '@ionic/angular';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { Observable, firstValueFrom } from 'rxjs';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { Product } from '../../../shared/models/product.model';
import { ProductSelectionData } from '../../../shared/models/usf-product-card.model';
import { SearchProductNumbersViewModel } from '../../model/search-product-numbers.model';
import { ListsOverlayService } from '../../services/lists-overlay.service';
import { ListsService } from '../../services/lists.service';
import { SearchProductNumbersService } from '../../services/search-product-numbers.service';
import { ProductRow } from '../../shared/list-detail-management/model/list-detail-management-view.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PATHS } from '../../../shared/constants/paths';
import { DetailHistoryService } from '../../../shared/components/header/history/detail-history.service';
import { SearchBarMultiLineComponent } from '../../../shared/components/search-bar-multi-line/search-bar-multi-line.component';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';

@Component({
  selector: 'app-search-product-numbers',
  templateUrl: './search-product-numbers.page.html',
  styleUrls: ['./search-product-numbers.page.scss'],
})
export class SearchProductNumbersPage implements ViewWillEnter, ViewDidLeave {
  @ViewChild(SearchBarMultiLineComponent, { static: false })
  multiLineSearchBar: SearchBarMultiLineComponent;
  searchProductNumbersViewModel$: Observable<SearchProductNumbersViewModel>;

  searchKey = '';
  productNumbersSet: Set<number> = new Set();
  readonly platformEnum = PlatformEnum;
  goBackToLists: boolean;
  mode: UsfProductCardModeEnum = UsfProductCardModeEnum.productNumberSearch;
  maxNumberOfProductNumbers = 1000;
  isSearchMaxExceeded = false;
  numberOfProductsScrubbed = 0;
  isSmallSearch = true;

  constructor(
    public platformService: PlatformService,
    private location: Location,
    private readonly router: Router,
    private searchProductNumbersService: SearchProductNumbersService,
    private historyService: DetailHistoryService,
    readonly addToListModalService: AddToListModalService,
  ) {}

  ionViewWillEnter(): void {
    this.searchProductNumbersViewModel$ =
      this.searchProductNumbersService.selectSearchProductNumbersViewModel();
    this.resetSearch();
    this.deselectAll();
    const previousUrl = this.historyService.getPreviousUrl();
    if (previousUrl?.includes('management')) {
      this.goBackToLists = false;
    } else {
      this.goBackToLists = true;
    }
    if (this.multiLineSearchBar) {
      this.multiLineSearchBar.ngOnInit();
    }
    this.searchProductNumbersService.trackSearchByProductNumberPageLoad();
  }

  ionViewDidLeave(): void {
    this.searchProductNumbersViewModel$ = undefined;
  }

  async openAddToListModal(
    searchProductNumbersViewModel: SearchProductNumbersViewModel,
  ) {
    const products: Product[] = [];
    for (const product of searchProductNumbersViewModel?.products) {
      if (!product.notFound && product.selected) {
        products.push(product);
      }
    }
    this.addToListModalService.openModal(products, true, true);
  }

  handleSearch(search: string) {
    this.searchProductNumbersService.searchForProducts(
      Array.from(this.productNumbersSet),
    );
    this.searchKey = search;
  }

  resetSearch() {
    this.searchProductNumbersService.resetProductNumberSearch();
    this.searchKey = '';
    this.clearMaxCheck();
  }

  selectProduct(selection: ProductSelectionData) {
    this.searchProductNumbersService.updateSelectionForProduct(
      selection.productNumber,
    );
  }

  deselectAll() {
    this.searchProductNumbersService.deselectAllProducts();
  }

  selectAll() {
    this.searchProductNumbersService.selectAllProducts();
  }

  goBack() {
    if (this.goBackToLists) {
      this.router.navigate([PATHS.LISTS]);
    } else {
      this.location.back();
    }
  }

  handlePaste(search: string) {
    this.searchKey = search;
  }

  clearMaxCheck() {
    this.isSearchMaxExceeded = false;
    this.numberOfProductsScrubbed = 0;
  }

  formatSearchInput = (search: string) => {
    let i = 0;
    let currentProductNumber = '';
    let formattedSearchString = '';
    this.productNumbersSet = new Set();
    this.isSearchMaxExceeded = false;
    while (i < search.length) {
      const currentCharacter = search.charAt(i);
      if (this.characterIsNumber(currentCharacter)) {
        currentProductNumber += currentCharacter;
      } else {
        if (this.isValidProductNumber(currentProductNumber)) {
          const productNumber = Number(currentProductNumber);
          if (
            this.productNumbersSet.size === this.maxNumberOfProductNumbers &&
            !this.productNumbersSet.has(productNumber)
          ) {
            this.isSearchMaxExceeded = true;
          }
          if (!this.productNumbersSet.has(productNumber)) {
            if (this.isSearchMaxExceeded) {
              this.numberOfProductsScrubbed++;
            } else {
              this.productNumbersSet.add(productNumber);
              formattedSearchString += currentProductNumber + ', ';
            }
          }
        }
        currentProductNumber = '';
      }

      if (i === search.length - 1) {
        if (this.isValidProductNumber(currentProductNumber)) {
          const productNumber = Number(currentProductNumber);
          if (
            this.productNumbersSet.size === this.maxNumberOfProductNumbers &&
            !this.productNumbersSet.has(productNumber)
          ) {
            this.isSearchMaxExceeded = true;
          }

          if (!this.productNumbersSet.has(productNumber)) {
            if (this.isSearchMaxExceeded) {
              this.numberOfProductsScrubbed++;
            } else {
              this.productNumbersSet.add(productNumber);
              formattedSearchString += currentProductNumber + ', ';
            }
          }
        }
      }

      i++;
    }
    return formattedSearchString;
  };

  closeAndDeatachAddToListModal = () => {
    this.addToListModalService.closeModal();
  };

  trackItem(index: any, item: ProductRow) {
    return item ? item.productNumber : index;
  }

  private isValidProductNumber(productNumber: string) {
    return productNumber.length >= 1 && productNumber.length <= 10;
  }

  private characterIsNumber(character: string) {
    return character >= '0' && character <= '9';
  }
}
